import { useQuery } from '@tanstack/react-query';
import { metadataIntegrationService } from '../services/metadataIntegrationService';


/**
 * Custom hook to fetch tables data for a connection using tiered metadata
 * @param {string} connectionId - The connection ID to fetch tables for
 * @param {Object} options - Additional options for the query
 * @returns {Object} Query result object with tables and tier status
 */
export const useTablesData = (connectionId, options = {}) => {
  const {
    enabled = !!connectionId,
    refetchInterval = false,
    forceFresh = false,
    database = null,
    schema = null,
    includeTierStatus = true,
    showAllCached = false,
    ...queryOptions
  } = options;

  return useQuery({
    queryKey: ['schema-tables-tiered', connectionId, { database, schema, includeTierStatus, showAllCached }],
    queryFn: async ({ signal }) => {
      // Use the integration service that fetches tables AND tier status in parallel
      const response = await metadataIntegrationService.getTableMetadataTiered(
        connectionId,
        { database, schema, show_all_cached: showAllCached },
        { forceFresh, signal }
      );
      return response;
    },
    enabled: enabled,
    refetchInterval: refetchInterval,
    // Use standard cache time for consistency
    staleTime: 10 * 60 * 1000, // 10 minutes
    ...queryOptions,
    select: (data) => {
      // metadataIntegrationService returns: {success, tables, count, tierStatus, errors}
      const tables = data?.tables || [];
      const tierStatus = data?.tierStatus || null;

      // Transform tables to include tier metadata visibility
      const enrichedTables = tables.map((table) => {
        return {
          // Core table info
          name: table.table_name,
          schema: table.table_schema,
          database: table.table_catalog, // Map database from table_catalog
          type: table.table_type || 'TABLE',
          row_count: table.row_count || 0,
          size_bytes: table.size_bytes || 0,

          // Tier metadata indicators
          has_schema_metadata: !!table.schema_collected_at,
          has_profiling: !!table.is_profiled,
          schema_collected_at: table.schema_collected_at,
          is_profiled: table.is_profiled,
          last_seen_at: table.last_seen_at,

          // Full table object for detailed views
          _raw: table
        };
      });

      return {
        tables: enrichedTables,
        tierStatus,
        count: enrichedTables.length
      };
    }
  });
};
